import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { money } from '@/lib/helpers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ComposedChart, Bar, Line, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, Cell,
} from 'recharts';
import {
  RefreshCw, LogOut, AlertTriangle, TrendingUp, Plus, MoreVertical, ShoppingCart,
  Wallet, Banknote, Receipt, Link2, Trophy, Users, ShieldCheck, Upload, Download,
  FileText, Palette, Pencil, Trash2, Eye, X, Check, Search, ArrowLeft, Globe, PlugZap,
  Sun, Moon, EyeOff, Minimize2, CalendarDays, PackageSearch,
} from 'lucide-react';

const MONTHS = ['all', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const MLABEL = { all: 'All months', 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec' };
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };

const Kpi = ({ label, value, sub, testid, big }) => (
  <div data-testid={testid} className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 break-all font-mono font-semibold leading-tight tabular-nums ${big ? 'text-lg sm:text-2xl' : 'text-base sm:text-lg'}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Card = ({ title, children, right, chart }) => (
  <section className="rounded-2xl border border-border bg-card p-4" {...(chart ? { 'data-chart-widget': '1' } : {})}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

const tip = { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 };

// ---------------------------------------------------------------------------
// Header action menus — 1:1 parity with shabzclosetdash.in (Quick Add + More).
// Only rendered for Owner/Manager (canManage); Partner sees a read-only header.
// Every item is wired to an EXISTING screen/route — no new destinations.
// ---------------------------------------------------------------------------
const MenuRow = ({ icon: Icon, label, onSelect, testid, tone }) => (
  <DropdownMenuItem data-testid={testid} onSelect={onSelect}
    className="cursor-pointer gap-2.5 py-2.5 text-sm">
    <Icon className={`h-4 w-4 ${tone || 'text-muted-foreground'}`} />
    <span>{label}</span>
  </DropdownMenuItem>
);

function DashboardActions({ isOwner, onIncentives }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode, toggleMode, hideCharts, toggleHideCharts, compact, toggleCompact } = useApp();
  const [quickPay, setQuickPay] = useState(false);
  const { data: webCfg } = useQuery({
    queryKey: ['website-integration'],
    queryFn: async () => (await api.get('/integrations/website')).data.data,
    enabled: !!isOwner,
  });
  const goto = (path) => navigate(path);
  const openWebsite = () => {
    const url = webCfg?.configuration?.website_url;
    if (url) window.open(url, '_blank', 'noopener');
    else { toast.message('Website not configured', { description: 'Set it in Admin → Integrations.' }); navigate('/integrations'); }
  };
  const scrollToStaff = () => {
    const el = document.querySelector('[data-testid="staff-incentives"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const exportExcel = async () => {
    try {
      const res = await api.get('/owner/export/sales', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `sales_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sales exported');
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="dashboard-actions">
      {/* QUICK ADD — green outline, 3-way menu (Purchase / Expense / Payment) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="quick-add-trigger"
            className="flex h-10 items-center gap-2 rounded-xl border border-primary/60 bg-primary/10 px-3 text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Quick Add</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Quick add</DropdownMenuLabel>
          <MenuRow testid="qa-purchase" icon={ShoppingCart} tone="text-primary" label="Purchase" onSelect={() => goto('/purchase')} />
          <MenuRow testid="qa-expense" icon={Wallet} tone="text-warning" label="Expense" onSelect={() => goto('/expenses')} />
          <MenuRow testid="qa-payment" icon={Banknote} tone="text-primary" label="Record payment" onSelect={() => setQuickPay(true)} />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* MORE — full menu set */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="more-trigger"
            className="flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-3 text-xs font-semibold uppercase tracking-wide text-foreground transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            <MoreVertical className="h-4 w-4" /> <span className="hidden sm:inline">More</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <MenuRow testid="more-purchase" icon={ShoppingCart} tone="text-primary" label="Purchase" onSelect={() => goto('/purchase')} />
          <MenuRow testid="more-expense" icon={Wallet} tone="text-warning" label="Expense" onSelect={() => goto('/expenses')} />
          <MenuRow testid="more-payment" icon={Banknote} tone="text-primary" label="Record payment" onSelect={() => setQuickPay(true)} />
          <DropdownMenuSeparator />
          <MenuRow testid="more-expense-log" icon={Receipt} label="Expense log" onSelect={() => goto('/expenses')} />
          <MenuRow testid="more-billing-sync" icon={Link2} label="Billing sync" onSelect={() => goto('/reports')} />
          <MenuRow testid="more-inventory-review" icon={PackageSearch} label="Inventory Review" onSelect={() => goto('/inventory-review')} />
          <MenuRow testid="more-leaderboard" icon={Trophy} label="Leaderboard" onSelect={() => (onIncentives ? onIncentives() : scrollToStaff())} />
          <MenuRow testid="more-incentives" icon={Users} label="Incentives" onSelect={() => (onIncentives ? onIncentives() : scrollToStaff())} />
          <DropdownMenuSeparator />
          {isOwner && <MenuRow testid="more-admin" icon={ShieldCheck} label="Admin" onSelect={() => goto('/admin')} />}
          {isOwner && <MenuRow testid="more-integrations" icon={PlugZap} label="Integrations" onSelect={() => goto('/integrations')} />}
          {isOwner && <MenuRow testid="more-website" icon={Globe} label="Website" onSelect={openWebsite} />}
          {isOwner && <MenuRow testid="more-import" icon={Upload} label="Bulk import" onSelect={() => goto('/data-management')} />}
          {isOwner && <MenuRow testid="more-excel" icon={Download} label="Excel export" onSelect={exportExcel} />}
          <MenuRow testid="more-pdf" icon={FileText} label="Print statement" onSelect={() => window.print()} />
          <DropdownMenuSeparator />
          <DropdownMenuSeparator />
          <MenuRow testid="more-theme" icon={mode === 'light' ? Moon : Sun} label={mode === 'light' ? 'Dark mode' : 'Light mode'} onSelect={(e) => { e.preventDefault(); toggleMode(); }} />
          <MenuRow testid="more-hide-charts" icon={hideCharts ? Eye : EyeOff} label={hideCharts ? 'Show charts' : 'Hide charts'} onSelect={(e) => { e.preventDefault(); toggleHideCharts(); }} />
          <MenuRow testid="more-compact" icon={Minimize2} label={compact ? 'Comfortable size' : 'Compact size'} onSelect={(e) => { e.preventDefault(); toggleCompact(); }} />
          <MenuRow testid="more-appearance" icon={Palette} label="Appearance" onSelect={() => goto('/settings')} />
        </DropdownMenuContent>
      </DropdownMenu>

      {quickPay && <QuickPaymentDialog onClose={() => setQuickPay(false)}
        onSaved={() => { queryClient.invalidateQueries({ queryKey: ['dashboard-full'] }); }} />}
    </div>
  );
}

// Quick Payment popup — settle a supplier without leaving the dashboard.
// Uses the tested billing endpoint POST /suppliers/{sid}/payment (an explicit
// financial action). Lists suppliers with an outstanding balance.
function QuickPaymentDialog({ onClose, onSaved }) {
  const [sid, setSid] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CASH');
  const [busy, setBusy] = useState(false);
  const { data: suppliers = [] } = useQuery({
    queryKey: ['qp-suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data.data,
  });
  const withDue = suppliers.filter((s) => Number(s.outstanding || 0) > 0);
  const selected = suppliers.find((s) => s.id === sid);
  const due = Number(selected?.outstanding || 0);

  const save = async () => {
    if (!sid) return toast.error('Choose a supplier');
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await api.post(`/suppliers/${sid}/payment`, { amount: amt, method });
      toast.success('Payment recorded');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record payment'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-payment-dialog">
        <DialogHeader>
          <DialogTitle>Quick payment</DialogTitle>
          <DialogDescription>Settle a supplier balance without leaving the dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Supplier</Label>
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger data-testid="qp-supplier" className="mt-1"><SelectValue placeholder={withDue.length ? 'Select supplier with balance' : 'No suppliers with a balance'} /></SelectTrigger>
              <SelectContent>
                {(withDue.length ? withDue : suppliers).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}{Number(s.outstanding || 0) > 0 ? ` · ${money(s.outstanding)} due` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected && <p className="text-xs text-muted-foreground">Outstanding: <span className="font-mono text-destructive">{money(due)}</span></p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input data-testid="qp-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="mt-1" />
              {due > 0 && <button type="button" data-testid="qp-fill-full" onClick={() => setAmount(String(due))} className="mt-1 rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-white/5">Pay full</button>}
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="qp-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="qp-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="qp-save">{busy ? 'Saving…' : 'Record payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DashboardBody() {
  const { user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.owner_access;
  const partnerScope = user?.role === 'PARTNER' ? (user?.dashboard_scope || 'all') : null;
  const [store, setStore] = useState(partnerScope || 'all');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('all');
  const [supplier, setSupplier] = useState('all');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['dashboard-full', store, year, month, supplier],
    queryFn: async () => (await api.get('/reports/dashboard-full', { params: { store, year: year || undefined, month, supplier } })).data.data,
    refetchInterval: 30000,
    keepPreviousData: true,
  });

  if (isError) {
    return (
      <div className="px-4 pt-10 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
        <p className="mt-3 text-sm font-semibold" data-testid="dash-error">Dashboard data unavailable</p>
        <button data-testid="dash-retry" onClick={() => refetch()} className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Retry</button>
      </div>
    );
  }

  const k = data?.kpi || {};
  const stores = data?.stores || [];
  const suppliers = data?.suppliers || [];
  const years = data?.years_available || [];
  const bd = (data?.business_data || []).filter((r) => r.sales || r.cogs || r.expenses);

  return (
    <div className="space-y-5 px-4 pt-4 pb-10" data-testid="management-dashboard">
      {/* Title row */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="mr-auto">
          <p className="text-xs text-muted-foreground">Source: {data?.source || 'BILLING'} · reads billing database</p>
          <h1 className="font-display text-xl font-semibold">Business Dashboard</h1>
          {data?.has_historical && (
            <span data-testid="badge-historical" className="mt-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Includes HISTORICAL + LIVE</span>
          )}
        </div>
      </div>
      {/* Filters row */}
      <div className="flex flex-wrap items-end gap-2">
        <FilterSel testid="f-store" label="Store" value={store} onChange={setStore} disabled={!!partnerScope && partnerScope !== 'all'}
          opts={[{ v: 'all', l: 'All Stores' }, ...stores.map((s) => ({ v: s.id, l: s.name }))]} />
        <FilterSel testid="f-year" label="Year" value={year || (years[years.length - 1] || '')} onChange={setYear}
          opts={(years.length ? years : [String(new Date().getFullYear())]).map((y) => ({ v: y, l: y }))} />
        <FilterSel testid="f-month" label="Month" value={month} onChange={setMonth} opts={MONTHS.map((m) => ({ v: m, l: MLABEL[m] }))} />
        <FilterSel testid="f-supplier" label="Supplier" value={supplier} onChange={setSupplier}
          opts={[{ v: 'all', l: 'All suppliers' }, ...suppliers.map((s) => ({ v: s.id, l: s.name }))]} />
        <button data-testid="dash-refresh" onClick={() => refetch()} className="flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-3 text-xs font-medium hover:bg-white/5">
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="dash-freshness">
        <span className={`h-2 w-2 rounded-full ${isFetching ? 'bg-warning' : 'bg-primary'}`} />
        {isLoading ? 'Loading…' : `Data updated: ${timeOf(data?.generated_at)}`}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi testid="kpi-sales" label="Sales" value={money(k.sales)} big sub={`${k.bills || 0} bills`} />
        <Kpi testid="kpi-cogs" label="COGS" value={money(k.cogs)} sub={`${k.cogs_pct || 0}%`} />
        <Kpi testid="kpi-gross" label="Gross Profit" value={money(k.gross_profit)} sub={`${k.gross_margin || 0}% margin`} />
        <Kpi testid="kpi-net" label="Net Profit" value={money(k.net_profit)} sub={`${k.net_margin || 0}% margin`} />
        <Kpi testid="kpi-expenses" label="Expenses" value={money(k.expenses)} />
        <Kpi testid="kpi-stock" label="Current Stock" value={money(k.current_stock)} sub={`${(k.stock_units || 0).toLocaleString('en-IN')} units · at cost`} />
        <Kpi testid="kpi-purchases" label="Purchases" value={money(k.purchases)} />
        <Kpi testid="kpi-payables" label="Payables Due" value={money(k.payables_due)} sub={`${k.open_bills || 0} open`} />
      </div>

      {/* Command centre extra */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi testid="kpi-avgbill" label="Avg Bill" value={money(k.avg_bill)} />
        <Kpi testid="kpi-cogspct" label="COGS %" value={`${k.cogs_pct || 0}%`} />
        <Kpi testid="kpi-grossmargin" label="Gross Margin" value={`${k.gross_margin || 0}%`} />
        <Kpi testid="kpi-netmargin" label="Net Margin" value={`${k.net_margin || 0}%`} />
      </div>

      {(data?.insights || []).length > 0 && (
        <Card title="Insights">
          <ul className="space-y-1 text-sm">
            {data.insights.map((t, i) => (<li key={i} className="flex gap-2"><TrendingUp className="mt-0.5 h-4 w-4 text-primary" /><span>{t}</span></li>))}
          </ul>
        </Card>
      )}

      {/* Month-by-month */}
      <Card chart title="Month by month">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data?.month_by_month || []} margin={{ left: -6, right: 8, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => money(v)} contentStyle={tip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="sales" name="Sales" fill="#c9a227" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cogs" name="COGS" fill="#d98032" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#8a8a99" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="net" name="Net" stroke="#5b9bd5" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card chart title="Store comparison">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.store_comparison || []} margin={{ left: -6, right: 8, top: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="store_name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => money(v)} contentStyle={tip} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sales" name="Sales" fill="#c9a227" radius={[3, 3, 0, 0]} />
                <Bar dataKey="gross" name="Gross" fill="#d98032" radius={[3, 3, 0, 0]} />
                <Bar dataKey="net" name="Net" fill="#5b9bd5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card chart title="Year comparison">
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="year-comparison">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Year</th><th>Sales</th><th>Gross</th><th>Net</th><th>Net %</th></tr></thead>
              <tbody>
                {(data?.year_comparison || []).map((y) => (
                  <tr key={y.year} className="border-t border-border"><td className="py-2 font-medium">{y.year}</td><td className="font-mono">{money(y.sales)}</td><td className="font-mono">{money(y.gross)}</td><td className="font-mono">{money(y.net)}</td><td className="font-mono">{y.net_margin}%</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card chart title="Purchases by supplier">
          <HBars rows={data?.purchases_by_supplier || []} color="#c9a227" empty="No purchase data in billing yet." />
        </Card>
        <Card chart title="Payables by supplier">
          <HBars rows={data?.payables_by_supplier || []} color="#d98032" empty="No payables." />
        </Card>
      </div>

      {/* Suppliers ledger */}
      <Card title="Suppliers ledger">
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="suppliers-ledger">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Supplier</th><th>Purchases</th><th>Paid</th><th>Balance Due</th></tr></thead>
            <tbody>
              {(data?.suppliers_ledger || []).length === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No suppliers in billing yet.</td></tr>
              ) : (data.suppliers_ledger.map((s) => (
                <tr key={s.supplier_id} className="border-t border-border"><td className="py-2 font-medium">{s.supplier_name}</td><td className="font-mono">{money(s.purchases)}</td><td className="font-mono">{money(s.paid)}</td><td className={`font-mono ${s.balance_due > 0 ? 'text-destructive' : ''}`}>{money(s.balance_due)}</td></tr>
              )))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Staff incentives */}
      <Card title="Staff performance & incentives" right={isOwner ? <span className="text-[11px] text-muted-foreground">Configure rule via Owner settings</span> : null}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="staff-incentives">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Staff</th><th>Sales</th><th>Target</th><th>Achv %</th><th>%</th><th>Earned</th><th>Paid</th><th>Pending</th></tr></thead>
            <tbody>
              {(data?.staff_incentives || []).map((s) => (
                <tr key={s.salesperson} className="border-t border-border"><td className="py-2 font-medium">{s.salesperson}</td><td className="font-mono">{money(s.sales)}</td><td className="font-mono">{money(s.target)}</td><td className="font-mono">{s.achievement}%</td><td className="font-mono">{s.pct}%</td><td className="font-mono">{money(s.incentive_earned)}</td><td className="font-mono">{money(s.incentive_paid)}</td><td className="font-mono">{money(s.incentive_pending)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data?.incentive_config?.default_pct || 0) === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">Incentive % not configured yet — earned shows 0 until the Owner sets the rule.</p>
        )}
      </Card>

      {/* Data tabs — Business Data / Purchases / Payables / Live Sales / Expenses */}
      <DataGridPanel data={data} canManage={canManage} isOwner={isOwner}
        refetch={refetch} store={store} year={year} month={month} bd={bd} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status chip — DUE (orange) / PARTIAL (blue) / PAID (green). Uses theme tokens.
// ---------------------------------------------------------------------------
const StatusChip = ({ status }) => {
  const s = (status || 'DUE').toUpperCase();
  const styles = {
    DUE: 'bg-warning/20 text-warning ring-1 ring-warning/40',
    PARTIAL: 'bg-sky-500/15 text-sky-400 ring-1 ring-sky-500/30',
    PAID: 'bg-primary/15 text-primary ring-1 ring-primary/30',
    UNPAID: 'bg-warning/20 text-warning ring-1 ring-warning/40',
  };
  return (
    <span data-testid={`status-${s.toLowerCase()}`}
      className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${styles[s] || styles.DUE}`}>
      {s === 'UNPAID' ? 'DUE' : s}
    </span>
  );
};

const PAY_METHODS = ['CASH', 'UPI', 'CARD', 'BANK'];

// ---------------------------------------------------------------------------
// Record-payment dialog — for a purchase (POST /purchases/{id}/payment) or a
// supplier balance (POST /suppliers/{sid}/payment). Reuses tested endpoints.
// ---------------------------------------------------------------------------
function PaymentDialog({ target, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CASH');
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const max = Number(target.balance || 0);
  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount.');
    setBusy(true);
    try {
      if (target.kind === 'purchase') {
        await api.post(`/purchases/${target.id}/payment`, { amount: amt, method });
      } else {
        await api.post(`/suppliers/${target.id}/payment`, { amount: amt, method });
      }
      toast.success('Payment recorded');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record payment');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment — {target.name}</DialogTitle>
          <DialogDescription>
            Balance due {money(max)}. This updates the supplier balance immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Amount</Label>
            <Input data-testid="payment-amount" type="number" value={amount} autoFocus
              onChange={(e) => setAmount(e.target.value)} placeholder="0" className="mt-1" />
            <div className="mt-1 flex gap-1">
              <button type="button" data-testid="payment-fill-full"
                onClick={() => setAmount(String(max))}
                className="rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-white/5">
                Pay full ({money(max)})
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="payment-method" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_METHODS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="payment-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="payment-save">
            {busy ? 'Saving…' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Supplier drill-down — totals + purchase history (GET /suppliers/{sid}).
// ---------------------------------------------------------------------------
function SupplierDrillDialog({ sid, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-drill', sid],
    queryFn: async () => (await api.get(`/suppliers/${sid}`)).data.data,
    enabled: !!sid,
  });
  const sup = data?.supplier || {};
  const purchases = data?.purchases || [];
  const totPurch = purchases.reduce((a, p) => a + Number(p.amount || 0), 0);
  const totPaid = purchases.reduce((a, p) => a + Number(p.paid || 0), 0);
  return (
    <Dialog open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="supplier-drill-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{sup.name || 'Supplier'}</DialogTitle>
          <DialogDescription>Purchase & payment history from the billing database.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">Purchases</p><p className="font-mono font-semibold">{money(totPurch)}</p></div>
              <div className="rounded-xl border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">Paid</p><p className="font-mono font-semibold">{money(totPaid)}</p></div>
              <div className="rounded-xl border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">Balance</p><p className={`font-mono font-semibold ${totPurch - totPaid > 0 ? 'text-destructive' : ''}`}>{money(totPurch - totPaid)}</p></div>
            </div>
            <div className="mt-2 max-h-72 overflow-y-auto">
              <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="supplier-drill-table">
                <thead className="sticky top-0 bg-card"><tr className="text-left text-xs text-muted-foreground"><th className="py-1">PO</th><th>Date</th><th>Amount</th><th>Paid</th><th>Status</th></tr></thead>
                <tbody>
                  {purchases.length === 0 ? (
                    <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No purchases recorded.</td></tr>
                  ) : purchases.map((p) => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="py-2 font-mono text-xs">{p.purchase_no || '—'}</td>
                      <td className="text-xs">{String(p.purchase_date || p.created_at || '').slice(0, 10)}</td>
                      <td className="font-mono">{money(p.amount)}</td>
                      <td className="font-mono">{money(p.paid)}</td>
                      <td><StatusChip status={p.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="supplier-drill-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sale add/edit dialog — creates via POST /sales, edits via PUT /sales/{inv}.
// Barcode stock reversal + KPI/profit recalculation are handled server-side.
// ---------------------------------------------------------------------------
function SaleDialog({ mode, sale, stores, defaultStore, onClose, onSaved }) {
  const { user } = useApp();
  const [storeId, setStoreId] = useState(
    mode === 'edit' ? sale?.store_id : (defaultStore && defaultStore !== 'all' ? defaultStore : (stores[0]?.id || '')));
  const [salesperson, setSalesperson] = useState(mode === 'edit' ? (sale?.salesperson || '') : (user?.username || ''));
  const [lines, setLines] = useState(
    mode === 'edit'
      ? (sale?.items || []).map((it, i) => ({ key: `${it.barcode}-${i}`, barcode: it.barcode, product: it.product, price: Number(it.price || 0), discount: Number(it.discount || 0) }))
      : []);
  const [payMethod, setPayMethod] = useState(mode === 'edit' ? (sale?.payments?.[0]?.method || 'CASH') : 'CASH');
  const [bc, setBc] = useState('');
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);

  const net = lines.reduce((a, l) => a + (Number(l.price || 0) - Number(l.discount || 0)), 0);

  const addBarcode = async () => {
    const code = bc.trim().toUpperCase();
    if (!code) return;
    if (lines.some((l) => l.barcode === code)) { toast.error('Already added'); setBc(''); return; }
    setLooking(true);
    try {
      const g = (await api.get(`/garments/lookup/${code}`)).data.data;
      if (mode === 'create' && g.status === 'SOLD') { toast.error(`${code} is already sold`); return; }
      if (storeId && g.store_id !== storeId) { toast.error(`${code} belongs to a different store`); return; }
      setLines((p) => [...p, { key: `${code}-${Date.now()}`, barcode: code, product: g.product, price: Number(g.price || 0), discount: 0 }]);
      setBc('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No garment for ${code}`);
    } finally { setLooking(false); }
  };

  const setLine = (key, field, val) => setLines((p) => p.map((l) => l.key === key ? { ...l, [field]: val } : l));
  const removeLine = (key) => setLines((p) => p.filter((l) => l.key !== key));

  const save = async () => {
    if (!storeId) return toast.error('Select a store');
    if (lines.length === 0) return toast.error('Add at least one item');
    setBusy(true);
    try {
      if (mode === 'create') {
        await api.post('/sales', {
          store_id: storeId, salesperson: salesperson || user?.username,
          items: lines.map((l) => ({ barcode: l.barcode, price: Number(l.price), discount: Number(l.discount || 0) })),
          bill_discount: 0, payments: [{ method: payMethod, amount: Math.round(net * 100) / 100 }],
        });
        toast.success('Sale recorded');
      } else {
        await api.put(`/sales/${sale.invoice_no}`, {
          items: lines.map((l) => ({ barcode: l.barcode, price: Number(l.price), discount: Number(l.discount || 0), final: Number(l.price) - Number(l.discount || 0) })),
          bill_discount: 0, payments: [{ method: payMethod, amount: Math.round(net * 100) / 100 }],
          salesperson: salesperson || undefined,
        });
        toast.success('Sale updated — stock synced');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save sale');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sale-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New live sale' : `Edit invoice ${sale?.invoice_no}`}</DialogTitle>
          <DialogDescription>
            Barcodes are validated against live stock. Removing an item returns it to stock; adding one marks it sold.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Store</Label>
              <Select value={storeId} onValueChange={setStoreId} disabled={mode === 'edit'}>
                <SelectTrigger data-testid="sale-store" className="mt-1"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Salesperson</Label>
              <Input data-testid="sale-salesperson" value={salesperson} onChange={(e) => setSalesperson(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Add item by barcode</Label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="sale-barcode-input" value={bc} onChange={(e) => setBc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBarcode(); } }}
                  placeholder="Scan or type barcode, press Enter" className="pl-8" />
              </div>
              <Button type="button" variant="secondary" onClick={addBarcode} disabled={looking} data-testid="sale-barcode-add">
                {looking ? '…' : 'Add'}
              </Button>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto rounded-xl border border-border">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="sale-lines">
              <thead className="sticky top-0 bg-card"><tr className="text-left text-xs text-muted-foreground"><th className="p-2">Barcode</th><th>Product</th><th>Price</th><th>Disc</th><th>Final</th><th></th></tr></thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No items yet.</td></tr>
                ) : lines.map((l) => (
                  <tr key={l.key} className="border-t border-border" data-testid={`sale-line-${l.barcode}`}>
                    <td className="p-2 font-mono text-xs">{l.barcode}</td>
                    <td className="text-xs">{l.product || '—'}</td>
                    <td><Input type="number" value={l.price} onChange={(e) => setLine(l.key, 'price', e.target.value)} className="h-8 w-20" data-testid={`sale-price-${l.barcode}`} /></td>
                    <td><Input type="number" value={l.discount} onChange={(e) => setLine(l.key, 'discount', e.target.value)} className="h-8 w-16" data-testid={`sale-disc-${l.barcode}`} /></td>
                    <td className="font-mono">{money(Number(l.price || 0) - Number(l.discount || 0))}</td>
                    <td><button onClick={() => removeLine(l.key)} data-testid={`sale-remove-${l.barcode}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="w-40">
              <Label className="text-xs">Payment</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="sale-pay-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Bill total</p>
              <p className="font-mono text-xl font-semibold" data-testid="sale-net">{money(net)}</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="sale-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="sale-save">
            {busy ? 'Saving…' : (mode === 'create' ? 'Record sale' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Delete confirmation — Owner only. Restores garments to stock server-side.
function DeleteSaleDialog({ sale, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.post('/sales/bulk-delete', { invoices: [sale.invoice_no] });
      toast.success('Sale deleted — stock restored');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-sale-dialog">
        <DialogHeader>
          <DialogTitle className="text-destructive">Delete invoice {sale.invoice_no}?</DialogTitle>
          <DialogDescription>
            This permanently removes the sale ({money(sale.net)}) and returns its {sale.items?.length || 0} item(s) to available stock. This is audited.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="delete-sale-cancel">Cancel</Button>
          <Button variant="destructive" onClick={del} disabled={busy} data-testid="delete-sale-confirm">
            {busy ? 'Deleting…' : 'Delete & restore stock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Live Sales tab — reads GET /sales for the current store/period, with
// add/edit/delete wired to the tested billing endpoints.
// ---------------------------------------------------------------------------
function LiveSalesTab({ store, year, month, canManage, canDelete, stores, onMutated }) {
  const navigate = useNavigate();
  const datePrefix = month && month !== 'all' ? `${year}-${String(Number(month)).padStart(2, '0')}` : `${year}`;
  const { data: sales = [], isLoading } = useQuery({
    queryKey: ['live-sales', store, datePrefix],
    queryFn: async () => (await api.get('/sales', { params: { store_id: store === 'all' ? '' : store, date: datePrefix, limit: 300 } })).data.data,
    keepPreviousData: true,
  });

  const storeName = (id) => stores.find((s) => s.id === id)?.name || id;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Live Sales <span className="text-muted-foreground">· {datePrefix}</span></h3>
        <span data-testid="live-sales-readonly" className="rounded-md bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">From Billing · read-only</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="live-sales-grid">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Invoice</th><th>Date</th><th>Store</th><th>Staff</th><th>Items</th><th>Net</th><th>Method</th><th></th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : sales.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No sales for this period.</td></tr>
            ) : sales.map((s) => (
              <tr key={s.invoice_no} className="border-t border-border" data-testid={`sale-row-${s.invoice_no}`}>
                <td className="py-2 font-mono text-xs">{s.invoice_no}</td>
                <td className="text-xs">{String(s.created_at || '').slice(0, 10)}</td>
                <td className="text-xs">{storeName(s.store_id)}</td>
                <td className="text-xs">{s.salesperson}</td>
                <td className="text-xs">{(s.items || []).length}</td>
                <td className="font-mono">{money(s.net)}</td>
                <td className="text-xs">{(s.payments || []).map((p) => p.method).join(', ')}</td>
                <td>
                  <div className="flex items-center justify-end gap-1.5">
                    <button title="View" data-testid={`sale-view-${s.invoice_no}`} onClick={() => navigate(`/invoice/${s.invoice_no}`)}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-foreground"><Eye className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business Data tab — inline add/edit/delete of monthly finance rows (Jan-Jun
// historical + any manual month). Dashboard-owned: writes ONLY to
// historical_financials, never to billing. Owner-editable.
// ---------------------------------------------------------------------------
const EMPTY_ROW = { year: String(new Date().getFullYear()), month: 1, store_id: '', sales: '', cogs: '', expenses: '' };
function BusinessDataTab({ isOwner, stores, onMutated }) {
  const [editing, setEditing] = useState(null);   // 'new' | rowId | null
  const [erow, setErow] = useState(EMPTY_ROW);
  const [bulk, setBulk] = useState(false);
  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['dash-historical'],
    queryFn: async () => (await api.get('/owner/historical')).data.data,
  });
  const storeName = (id) => (id === 'all' ? 'All Stores' : (stores.find((s) => s.id === id)?.name || id));

  const startNew = () => { setErow({ ...EMPTY_ROW, store_id: 'all' }); setEditing('new'); };
  const startEdit = (r) => { setErow({ year: String(r.year), month: r.month, store_id: r.store_id, sales: r.sales, cogs: r.cogs, expenses: r.expenses }); setEditing(r.id); };
  const cancel = () => { setEditing(null); setErow(EMPTY_ROW); };
  const save = async () => {
    if (!erow.store_id) return toast.error('Select a store');
    const body = { year: erow.year, month: erow.month, store: erow.store_id,
      sales: Number(erow.sales || 0), cogs: Number(erow.cogs || 0), expenses: Number(erow.expenses || 0) };
    try {
      if (editing === 'new') await api.post('/owner/historical', body);
      else await api.put(`/owner/historical/${editing}`, body);
      toast.success('Saved'); cancel(); refetch(); onMutated && onMutated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed (Owner only)'); }
  };
  const del = async (id) => {
    try { await api.delete(`/owner/historical/${id}`); toast.success('Deleted'); refetch(); onMutated && onMutated(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const Editor = () => (
    <tr className="border-t border-border bg-primary/5" data-testid="business-row-editor">
      <td className="p-1"><Input className="h-8 w-20" value={erow.year} onChange={(e) => setErow({ ...erow, year: e.target.value })} data-testid="bd-year" /></td>
      <td className="p-1">
        <Select value={String(erow.month)} onValueChange={(v) => setErow({ ...erow, month: Number(v) })}>
          <SelectTrigger className="h-8 w-24" data-testid="bd-month"><SelectValue /></SelectTrigger>
          <SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (<SelectItem key={m} value={String(m)}>{MLABEL[m]}</SelectItem>))}</SelectContent>
        </Select>
      </td>
      <td className="p-1">
        <Select value={erow.store_id} onValueChange={(v) => setErow({ ...erow, store_id: v })}>
          <SelectTrigger className="h-8 w-32" data-testid="bd-store"><SelectValue placeholder="Store" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Stores</SelectItem>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
        </Select>
      </td>
      <td className="p-1"><Input type="number" className="h-8 w-24" value={erow.sales} onChange={(e) => setErow({ ...erow, sales: e.target.value })} data-testid="bd-sales" /></td>
      <td className="p-1"><Input type="number" className="h-8 w-24" value={erow.cogs} onChange={(e) => setErow({ ...erow, cogs: e.target.value })} data-testid="bd-cogs" /></td>
      <td className="p-1 text-xs text-muted-foreground">auto</td>
      <td className="p-1"><Input type="number" className="h-8 w-24" value={erow.expenses} onChange={(e) => setErow({ ...erow, expenses: e.target.value })} data-testid="bd-expenses" /></td>
      <td className="p-1 text-xs text-muted-foreground">auto</td>
      <td className="p-1">
        <div className="flex gap-1">
          <button onClick={save} data-testid="bd-save" className="rounded-md p-1.5 text-primary hover:bg-white/5"><Check className="h-4 w-4" /></button>
          <button onClick={cancel} data-testid="bd-cancel" className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5"><X className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Business Data (monthly)</h3>
          <p className="text-[11px] text-muted-foreground">Manual monthly figures (e.g. Jan–Jun). Dashboard-only · never changes billing.</p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <button data-testid="business-bulk-import" onClick={() => setBulk(true)}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5">
              <Upload className="h-4 w-4" /> Bulk Import
            </button>
            <button data-testid="business-add-row" onClick={startNew}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
              <Plus className="h-4 w-4" /> Add Row
            </button>
          </div>
        )}
      </div>
      <div className="-mx-4 overflow-x-auto px-4">
        <table className="min-w-[720px] w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="business-data">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Year</th><th>Month</th><th>Store</th><th>Sales</th><th>COGS</th><th>Gross</th><th>Expenses</th><th>Net</th><th></th></tr></thead>
          <tbody>
            {editing === 'new' && <Editor />}
            {isLoading ? (
              <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 && editing !== 'new' ? (
              <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No monthly data yet. {isOwner ? 'Use “Add Row”.' : ''}</td></tr>
            ) : rows.map((r) => editing === r.id ? <Editor key={r.id} /> : (
              <tr key={r.id} className="border-t border-border" data-testid={`business-row-${r.id}`}>
                <td className="py-2">{r.year}</td>
                <td>{MLABEL[r.month] || r.month}</td>
                <td>{storeName(r.store_id)}</td>
                <td className="font-mono">{money(r.sales)}</td>
                <td className="font-mono">{money(r.cogs)}</td>
                <td className="font-mono">{money((r.sales || 0) - (r.cogs || 0))}</td>
                <td className="font-mono">{money(r.expenses)}</td>
                <td className="font-mono">{money(r.net != null ? r.net : (r.sales || 0) - (r.cogs || 0) - (r.expenses || 0))}</td>
                <td>
                  {isOwner && (
                    <div className="flex items-center justify-end gap-1.5">
                      <button title="Edit" data-testid={`business-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                      <button title="Delete" data-testid={`business-delete-${r.id}`} onClick={() => del(r.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bulk && <BusinessDataBulkDialog stores={stores} onClose={() => setBulk(false)}
        onSaved={() => { refetch(); onMutated && onMutated(); }} />}
    </div>
  );
}

// Business Data (historical) bulk import — uses /owner/historical/import which
// previews with commit=false then writes with commit=true (all-or-nothing).
function BusinessDataBulkDialog({ stores, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const template = async () => {
    try {
      const res = await api.get('/owner/historical/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'Business Data Import Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  };
  const validate = async () => {
    if (!file) return toast.error('Choose an Excel/CSV file first.');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/owner/historical/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!file || !preview || preview.error_count > 0 || preview.valid_rows < 1) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/owner/historical/import?commit=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data.data); toast.success(`Imported ${preview.valid_rows} row(s).`); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="business-bulk-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Import Business Data</DialogTitle>
          <DialogDescription>
            Upload XLSX/CSV (year, month, store, sales, cogs, expenses). Use "All Stores" for a combined row.
            Re-importing the same year/month/store updates it (no duplicates). Nothing is saved until you confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">File</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" className="mt-1" data-testid="bdi-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} /></div>
          <Button variant="outline" size="sm" onClick={template} data-testid="bdi-template"><Download className="mr-2 h-4 w-4" />Template</Button>
          <Button size="sm" onClick={validate} disabled={busy} data-testid="bdi-validate">Validate &amp; Preview</Button>
        </div>
        {preview && !result && (
          <div className="space-y-2 rounded-lg border border-border p-3" data-testid="bdi-summary">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <DpStat label="Valid rows" value={preview.valid_rows} />
              <DpStat label="Errors" value={preview.error_count} />
            </div>
            {preview.error_count > 0 && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs" data-testid="bdi-errors">
                <div className="mb-1 font-semibold text-destructive">Fix these before importing (all-or-nothing):</div>
                <ul className="max-h-32 list-disc space-y-0.5 overflow-auto pl-5">
                  {(preview.errors || []).slice(0, 30).map((e, i) => (<li key={i}>{typeof e === 'string' ? e : (e.error || JSON.stringify(e))}</li>))}
                </ul>
              </div>
            )}
            <p className="text-sm text-muted-foreground">{preview.valid_rows} row(s) ready. Rows with the same year/month/store will be updated.</p>
          </div>
        )}
        {result && (
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm" data-testid="bdi-result">
            <div className="font-semibold text-primary">Import complete</div>
            <div className="mt-1">Imported {preview?.valid_rows} row(s).</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={commit} disabled={busy || !preview || preview.error_count > 0 || preview.valid_rows < 1} data-testid="bdi-confirm">
              {busy ? 'Working…' : `Confirm Import${preview ? ` (${preview.valid_rows})` : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// Writes ONLY to dash_expenses; feeds dashboard numbers, never billing.
// ---------------------------------------------------------------------------
const EMPTY_EXP = { date: new Date().toISOString().slice(0, 10), store_id: '', category: '', amount: '' };
function ExpensesTab({ canManage, stores, categorySummary, onMutated }) {
  const [editing, setEditing] = useState(null);
  const [er, setEr] = useState(EMPTY_EXP);
  const [bulk, setBulk] = useState(false);
  const [undoImp, setUndoImp] = useState(false);
  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['dash-expenses'], queryFn: async () => (await api.get('/dashboard/expenses')).data.data,
  });
  const { data: expCats = [] } = useQuery({
    queryKey: ['exp-cats'], queryFn: async () => (await api.get('/expense-categories')).data.data,
  });
  const storeName = (id) => (id === 'all' ? 'All Stores' : (stores.find((s) => s.id === id)?.name || '\u2014'));
  const startNew = () => { setEr({ ...EMPTY_EXP, store_id: 'all' }); setEditing('new'); };
  const startEdit = (r) => { setEr({ date: r.date, store_id: r.store_id, category: r.category, amount: r.amount }); setEditing(r.id); };
  const cancel = () => { setEditing(null); setEr(EMPTY_EXP); };
  const save = async () => {
    if (!er.category) return toast.error('Enter a category');
    const body = { date: er.date, store_id: er.store_id, category: er.category, amount: Number(er.amount || 0), year: er.date.slice(0, 4), month: Number(er.date.slice(5, 7)) };
    try {
      if (editing === 'new') await api.post('/dashboard/expenses', body);
      else await api.put(`/dashboard/expenses/${editing}`, body);
      toast.success('Saved'); cancel(); refetch(); onMutated && onMutated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => { try { await api.delete(`/dashboard/expenses/${id}`); toast.success('Deleted'); refetch(); onMutated && onMutated(); } catch { toast.error('Delete failed'); } };

  const Editor = () => (
    <tr className="border-t border-border bg-primary/5" data-testid="expense-row-editor">
      <td className="p-1"><Input type="date" className="h-8 w-36" value={er.date} onChange={(e) => setEr({ ...er, date: e.target.value })} data-testid="exp-date" /></td>
      <td className="p-1">
        <Select value={er.store_id} onValueChange={(v) => setEr({ ...er, store_id: v })}>
          <SelectTrigger className="h-8 w-32" data-testid="exp-store"><SelectValue placeholder="Store" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Stores</SelectItem>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
        </Select>
      </td>
      <td className="p-1"><Input className="h-8 w-32" list="exp-cat-list" value={er.category} onChange={(e) => setEr({ ...er, category: e.target.value })} placeholder="Rent, Salary…" data-testid="exp-category" />
        <datalist id="exp-cat-list">{expCats.map((c) => (<option key={c.id} value={c.name} />))}</datalist></td>
      <td className="p-1"><Input type="number" className="h-8 w-24" value={er.amount} onChange={(e) => setEr({ ...er, amount: e.target.value })} data-testid="exp-amount" /></td>
      <td className="p-1"><div className="flex gap-1"><button onClick={save} data-testid="exp-save" className="rounded-md p-1.5 text-primary hover:bg-white/5"><Check className="h-4 w-4" /></button><button onClick={cancel} data-testid="exp-cancel" className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5"><X className="h-4 w-4" /></button></div></td>
    </tr>
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div><h3 className="text-sm font-semibold">Dashboard expenses</h3>
          <p className="text-[11px] text-muted-foreground">Dashboard-only spends. Never changes billing.</p></div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button data-testid="expenses-bulk-import" onClick={() => setBulk(true)} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5"><Upload className="h-4 w-4" /> Bulk Import</button>
            <button data-testid="expenses-undo-import" onClick={() => setUndoImp(true)} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5"><Trash2 className="h-4 w-4" /> Undo Import</button>
            <button data-testid="expense-add-row" onClick={startNew} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Add Row</button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="dash-expenses-grid">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Date</th><th>Store</th><th>Category</th><th>Amount</th><th></th></tr></thead>
          <tbody>
            {editing === 'new' && <Editor />}
            {isLoading ? (<tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Loading…</td></tr>)
              : rows.length === 0 && editing !== 'new' ? (<tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No dashboard expenses yet.</td></tr>)
              : rows.map((r) => editing === r.id ? <Editor key={r.id} /> : (
                <tr key={r.id} className="border-t border-border" data-testid={`expense-row-${r.id}`}>
                  <td className="py-2 text-xs">{r.date}</td>
                  <td>{storeName(r.store_id)}</td>
                  <td className="font-medium">{r.category}</td>
                  <td className="font-mono">{money(r.amount)}</td>
                  <td>{canManage && (
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`expense-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`expense-delete-${r.id}`} onClick={() => del(r.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {(categorySummary || []).length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">All expenses by category (billing + dashboard)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="expense-categories">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Category</th><th>Amount</th></tr></thead>
              <tbody>{categorySummary.map((e, i) => (<tr key={i} className="border-t border-border"><td className="py-2 font-medium">{e.category}</td><td className="font-mono">{money(e.amount)}</td></tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
      {bulk && <DashBulkImportDialog stores={stores} onClose={() => setBulk(false)} onSaved={() => { refetch(); onMutated && onMutated(); }}
        cfg={{ title: 'Bulk Import Expenses', noun: 'expense',
          validateUrl: '/dashboard/expenses/import/validate', commitUrl: '/dashboard/expenses/import/commit',
          templateUrl: '/dashboard/expenses/template', templateFile: 'Expense Import Template.xlsx',
          totalsFields: [{ key: 'amount', label: 'Total amount' }],
          desc: 'Upload XLSX/CSV of dashboard expenses (date, store, category, amount). Use "All Stores" for a combined entry. Dashboard-only; never affects billing. Nothing is saved until you confirm.' }} />}
      {undoImp && <ImportHistoryModal onClose={() => setUndoImp(false)} onSaved={() => { refetch(); onMutated && onMutated(); }}
        historyUrl="/dashboard/expenses/import/history" rollbackUrl="/dashboard/expenses/import/rollback" remapUrl="/dashboard/expenses/import/remap" unit="expense"
        desc="Remove every expense a past Excel import added. Expenses added manually are never affected." />}
    </div>
  );
}

// Dashboard-only purchase dialog (create/edit) — supplier is free-text so you
// can add a supplier on the fly. Writes ONLY to dash_purchases.
function DashPurchaseDialog({ mode, entry, stores, onClose, onSaved }) {
  const [date, setDate] = useState(mode === 'edit' ? entry.date : new Date().toISOString().slice(0, 10));
  const [storeId, setStoreId] = useState(mode === 'edit' ? (entry.store_id || 'all') : 'all');
  const [supplier, setSupplier] = useState(mode === 'edit' ? entry.supplier_name : '');
  const [amount, setAmount] = useState(mode === 'edit' ? String(entry.amount) : '');
  const [paid, setPaid] = useState(mode === 'edit' ? String(entry.paid) : '');
  const [busy, setBusy] = useState(false);
  const { data: knownSuppliers = [] } = useQuery({
    queryKey: ['dash-suppliers'], queryFn: async () => (await api.get('/dashboard/suppliers')).data.data,
  });
  const balance = Number(amount || 0) - Number(paid || 0);
  const save = async () => {
    if (!supplier.trim()) return toast.error('Enter a supplier');
    const body = { date, store_id: storeId, supplier, amount: Number(amount || 0), paid: Number(paid || 0), year: date.slice(0, 4), month: Number(date.slice(5, 7)) };
    setBusy(true);
    try {
      if (mode === 'edit') await api.put(`/dashboard/purchases/${entry.id}`, body);
      else await api.post('/dashboard/purchases', body);
      toast.success('Saved'); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dash-purchase-dialog">
        <DialogHeader><DialogTitle>{mode === 'edit' ? 'Edit purchase' : 'Add purchase'}</DialogTitle>
          <DialogDescription>Dashboard-only purchase & supplier. Does not affect billing stock.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="dp-date" /></div>
          <div><Label className="text-xs">Store</Label>
            <Select value={storeId} onValueChange={setStoreId}><SelectTrigger className="mt-1" data-testid="dp-store"><SelectValue placeholder="Store" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Stores</SelectItem>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-xs">Supplier</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier name" className="mt-1" data-testid="dp-supplier" list="dash-supplier-list" />
            <datalist id="dash-supplier-list">
              {knownSuppliers.map((n) => (<option key={n} value={n} />))}
            </datalist>
            {knownSuppliers.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="dp-supplier-chips">
                {knownSuppliers.slice(0, 8).map((n) => (
                  <button key={n} type="button" onClick={() => setSupplier(n)}
                    className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${supplier === n ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted-foreground hover:bg-white/5'}`}>
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div><Label className="text-xs">Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="dp-amount" /></div>
          <div><Label className="text-xs">Paid</Label><Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} className="mt-1" data-testid="dp-paid" /></div>
        </div>
        <p className="text-sm text-muted-foreground">Balance: <span className={`font-mono font-semibold ${balance > 0 ? 'text-destructive' : 'text-primary'}`} data-testid="dp-balance">{money(balance)}</span></p>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="dp-save">{busy ? 'Saving…' : 'Save'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Bulk import for dashboard-only purchases (XLSX/CSV). Preview first, then a
// required confirm step. Writes only to dash_purchases; never affects stock.
function DashBulkImportDialog({ stores, onClose, onSaved, cfg }) {
  const C = cfg || {
    title: 'Bulk Import Purchases', noun: 'purchase',
    validateUrl: '/dashboard/purchases/import/validate',
    commitUrl: '/dashboard/purchases/import/commit',
    templateUrl: '/dashboard/purchases/template', templateFile: 'Purchase Import Template.xlsx',
    totalsFields: [{ key: 'amount', label: 'Total amount' }, { key: 'paid', label: 'Total paid' }, { key: 'balance', label: 'Balance' }],
    desc: 'Upload XLSX/CSV of dashboard purchases. Use "All Stores" for a purchase covering both stores. This does not affect billing stock. Nothing is saved until you confirm.',
  };
  const [defaultStore, setDefaultStore] = useState('all');
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const template = async () => {
    try {
      const res = await api.get(C.templateUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = C.templateFile;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  };

  const validate = async () => {
    if (!file) return toast.error('Choose an Excel/CSV file first.');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (defaultStore) fd.append('default_store_id', defaultStore);
      const r = await api.post(C.validateUrl, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setSummary(r.data.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!summary || summary.to_import < 1) return;
    setBusy(true);
    try {
      const r = await api.post(C.commitUrl, {
        rows: summary.rows, filename: summary.filename, batch_key: summary.batch_key,
        total_rows: summary.total_rows,
      });
      setResult(r.data.data);
      if (r.data.data.idempotent_replay) toast.info('This file was already imported — no duplicates.');
      else toast.success(`Imported ${r.data.data.created} ${C.noun}(s).`);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const errCsv = () => {
    if (!summary) return;
    const lines = [['row', 'error']];
    (summary.row_errors || []).forEach((e) => (e.errors || []).forEach((er) => lines.push([e.row, er])));
    const csv = lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'purchase_import_errors.csv';
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const t = summary?.totals || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="purchase-bulk-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{C.title}</DialogTitle>
          <DialogDescription>
            {C.desc}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <Label className="text-xs">Default store (for blank rows)</Label>
            <Select value={defaultStore} onValueChange={setDefaultStore}>
              <SelectTrigger className="mt-1" data-testid="pbi-default-store"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Stores</SelectItem>
                {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">File</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" className="mt-1" data-testid="pbi-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); setResult(null); }} />
          </div>
          <Button variant="outline" size="sm" onClick={template} data-testid="pbi-template"><Download className="mr-2 h-4 w-4" />Template</Button>
          <Button size="sm" onClick={validate} disabled={busy} data-testid="pbi-validate">Validate &amp; Preview</Button>
        </div>

        {summary && !result && (
          <div className="space-y-3 rounded-lg border border-border p-3" data-testid="pbi-summary">
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
              <DpStat label="Rows" value={summary.total_rows} />
              <DpStat label="Valid" value={summary.valid_rows} />
              <DpStat label="Invalid" value={summary.invalid_rows} />
              <DpStat label="To import" value={summary.to_import} />
              {C.totalsFields.map((f) => (<DpStat key={f.key} label={f.label} value={money(t[f.key])} />))}
            </div>
            {summary.row_errors?.length > 0 && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs" data-testid="pbi-errors">
                <div className="mb-1 font-semibold text-destructive">{summary.invalid_rows} row(s) have errors and will be skipped:</div>
                <ul className="max-h-32 list-disc space-y-0.5 overflow-auto pl-5">
                  {summary.row_errors.slice(0, 30).map((e, i) => (<li key={i}>Row {e.row}: {(e.errors || []).join('; ')}</li>))}
                </ul>
                <Button size="sm" variant="outline" className="mt-2" onClick={errCsv} data-testid="pbi-error-report"><Download className="mr-2 h-4 w-4" />Error report</Button>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {summary.to_import} {C.noun}(s) will be created, totalling {money(t.amount ?? t[C.totalsFields?.[0]?.key])}.
            </p>
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm" data-testid="pbi-result">
            <div className="flex items-center gap-2 font-semibold text-primary"><Check className="h-4 w-4" />
              {result.idempotent_replay ? 'Already imported' : 'Import complete'}</div>
            <div className="mt-1">Created {result.created} · Skipped {result.skipped}. Batch <span className="font-mono text-xs">{(result.batch_id || '').slice(0, 8)}</span></div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={commit} disabled={busy || !summary || summary.to_import < 1} data-testid="pbi-confirm">
              {busy ? 'Working…' : `Confirm Import${summary ? ` (${summary.to_import})` : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DpStat = ({ label, value }) => (
  <div className="rounded-md bg-muted/40 p-2">
    <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
    <div className="font-semibold">{value}</div>
  </div>
);


// ---------------------------------------------------------------------------
// Tabbed data panel — Business Data / Purchases / Payables / Live Sales / Expenses.
// ---------------------------------------------------------------------------
function DataGridPanel({ data, canManage, isOwner, refetch, store, year, month, bd }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('business');
  const [payTarget, setPayTarget] = useState(null);
  const [drillSid, setDrillSid] = useState(null);
  const [dashPur, setDashPur] = useState(null);
  const [bulkPur, setBulkPur] = useState(false);
  const [undoPur, setUndoPur] = useState(false);

  const purchases = data?.purchases_grid || [];
  const payables = data?.payables_grid || [];
  const expenses = data?.expense_categories || [];
  const stores = data?.stores || [];
  const kpiBills = data?.kpi?.bills || 0;

  const TABS = [
    { id: 'business', label: `Business Data (${bd.length})` },
    { id: 'purchases', label: `Purchases (${purchases.length})` },
    { id: 'payables', label: `Payables (${payables.length})` },
    { id: 'sales', label: `Live Sales (${kpiBills})` },
    { id: 'expenses', label: `Expenses (${expenses.length})` },
  ];

  return (
    <section className="rounded-2xl border border-border bg-card p-4" data-testid="data-grid-panel">
      <div className="mb-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`grid-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'business' && (
        <BusinessDataTab isOwner={isOwner} stores={stores} onMutated={() => refetch()} />
      )}

      {tab === 'purchases' && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Purchases</h3>
              <p className="text-[11px] text-muted-foreground">Billing rows are read-only; “Add” creates a dashboard-only purchase.</p>
            </div>
            {canManage && (
              <div className="flex items-center gap-2">
                <button data-testid="purchases-bulk-import" onClick={() => setBulkPur(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5">
                  <Upload className="h-4 w-4" /> Bulk Import
                </button>
                <button data-testid="purchases-undo-import" onClick={() => setUndoPur(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5">
                  <Trash2 className="h-4 w-4" /> Undo Import
                </button>
                <button data-testid="purchases-add-row" onClick={() => setDashPur({ mode: 'create' })}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="purchases-grid">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Year</th><th>Month</th><th>Store</th><th>Supplier</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {purchases.length === 0 ? (
                  <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No purchases yet. Use “Add” to record a dashboard purchase.</td></tr>
                ) : purchases.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`purchase-row-${p.id}`}>
                    <td className="py-2">{p.year}</td>
                    <td>{p.month || '\u2014'}</td>
                    <td>{p.store_name}</td>
                    <td>
                      {p.source === 'DASH'
                        ? <span className="font-medium">{p.supplier_name} <span className="ml-1 rounded bg-muted/40 px-1 text-[9px] uppercase text-muted-foreground">dash</span></span>
                        : <button className="font-medium text-primary hover:underline" data-testid={`purchase-supplier-${p.id}`}
                            onClick={() => p.supplier_id && setDrillSid(p.supplier_id)}>{p.supplier_name}</button>}
                    </td>
                    <td className="font-mono">{money(p.amount)}</td>
                    <td className="font-mono text-primary">{money(p.paid)}</td>
                    <td className={`font-mono ${p.outstanding > 0 ? 'text-destructive' : ''}`}>{money(p.outstanding)}</td>
                    <td><StatusChip status={p.status} /></td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        {p.source === 'DASH' ? (
                          canManage && (
                            <>
                              <button title="Edit" data-testid={`dashpur-edit-${p.id}`} onClick={() => setDashPur({ mode: 'edit', entry: p })}
                                className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                              <button title="Delete" data-testid={`dashpur-del-${p.id}`} onClick={async () => { try { await api.delete(`/dashboard/purchases/${p.id}`); toast.success('Deleted'); refetch(); } catch { toast.error('Delete failed'); } }}
                                className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                            </>
                          )
                        ) : (
                          <>
                            <button title="View items" data-testid={`purchase-view-${p.id}`} onClick={() => navigate(`/purchases/${p.id}`)}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-foreground"><Eye className="h-4 w-4" /></button>
                            {canManage && p.outstanding > 0 && (
                              <button title="Record payment" data-testid={`purchase-pay-${p.id}`}
                                onClick={() => setPayTarget({ kind: 'purchase', id: p.id, name: p.supplier_name, balance: p.outstanding })}
                                className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'payables' && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Payables</h3>
            {canManage && (
              <div className="flex items-center gap-2">
                <button data-testid="payables-bulk-import" onClick={() => setBulkPur(true)} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white/5"><Upload className="h-4 w-4" /> Bulk Import</button>
                <button data-testid="payables-add-row" onClick={() => setDashPur({ mode: 'create' })} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Add</button>
              </div>
            )}
          </div>
          <p className="mb-2 text-xs text-muted-foreground">Add or import a payable as a supplier purchase with an outstanding balance (it also appears under Purchases). Use "All Stores" if it spans both stores.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="payables-grid">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Supplier</th><th>Amount Payable</th><th>Paid</th><th>Balance</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {payables.length === 0 ? (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No outstanding payables. All suppliers settled.</td></tr>
                ) : payables.map((p) => (
                  <tr key={p.supplier_id} className="border-t border-border" data-testid={`payable-row-${p.supplier_id}`}>
                    <td className="py-2">
                      {p.source === 'DASH'
                        ? <span className="font-medium">{p.supplier_name} <span className="ml-1 rounded bg-muted/40 px-1 text-[9px] uppercase text-muted-foreground">dash</span></span>
                        : <button className="font-medium text-primary hover:underline" data-testid={`payable-supplier-${p.supplier_id}`}
                            onClick={() => setDrillSid(p.supplier_id)}>{p.supplier_name}</button>}
                    </td>
                    <td className="font-mono">{money(p.amount_payable)}</td>
                    <td className="font-mono text-primary">{money(p.paid)}</td>
                    <td className="font-mono text-destructive">{money(p.balance)}</td>
                    <td><StatusChip status={p.status} /></td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        {canManage && p.source !== 'DASH' && (
                          <button title="Record payment" data-testid={`payable-pay-${p.supplier_id}`}
                            onClick={() => setPayTarget({ kind: 'supplier', id: p.supplier_id, name: p.supplier_name, balance: p.balance })}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                        )}
                        {p.source !== 'DASH' && (
                          <button title="View history" data-testid={`payable-view-${p.supplier_id}`} onClick={() => setDrillSid(p.supplier_id)}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-foreground"><Eye className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'sales' && (
        <LiveSalesTab store={store} year={year} month={month}
          canManage={canManage} canDelete={isOwner} stores={stores} onMutated={() => refetch()} />
      )}

      {tab === 'expenses' && (
        <ExpensesTab canManage={canManage} stores={stores} categorySummary={expenses} onMutated={() => refetch()} />
      )}

      {payTarget && <PaymentDialog target={payTarget} onClose={() => setPayTarget(null)} onSaved={() => refetch()} />}
      {drillSid && <SupplierDrillDialog sid={drillSid} onClose={() => setDrillSid(null)} />}
      {dashPur && <DashPurchaseDialog mode={dashPur.mode} entry={dashPur.entry} stores={stores}
        onClose={() => setDashPur(null)} onSaved={() => refetch()} />}
      {bulkPur && <DashBulkImportDialog stores={stores}
        onClose={() => setBulkPur(false)} onSaved={() => refetch()} />}
      {undoPur && <ImportHistoryModal onClose={() => setUndoPur(false)} onSaved={() => refetch()}
        historyUrl="/dashboard/purchases/import/history" rollbackUrl="/dashboard/purchases/import/rollback" remapUrl="/dashboard/purchases/import/remap" unit="purchase"
        desc="Remove every purchase a past Excel import added. Purchases added manually are never affected." />}
    </section>
  );
}


const FilterSel = ({ label, value, onChange, opts, testid, disabled }) => (
  <div>
    <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-10 w-[130px] rounded-xl" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map((o) => (<SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>))}</SelectContent>
    </Select>
  </div>
);

const HBars = ({ rows, color, empty }) => {
  if (!rows.length) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={rows} margin={{ left: 20, right: 12 }}>
          <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis type="category" dataKey="supplier" width={90} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
          <Tooltip formatter={(v) => money(v)} contentStyle={tip} />
          <Bar dataKey="amount" radius={[0, 3, 3, 0]}>{rows.map((r, i) => (<Cell key={i} fill={color} />))}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ===========================================================================
// INCENTIVES (dashboard-owned) — Manage Staff, Fix %, per-store manual logs,
// KPIs and staff chart. Never writes to billing.
// ===========================================================================
function StaffRow({ s, onSave, onDelete, busy }) {
  const [f, setF] = useState({
    name: s.name || '', phone: s.phone || '', role: s.role || '',
    monthly_target: s.monthly_target ?? '', commission_pct: s.commission_pct ?? '',
  });
  const dirty = f.name !== (s.name || '') || f.phone !== (s.phone || '') || f.role !== (s.role || '')
    || String(f.monthly_target) !== String(s.monthly_target ?? '') || String(f.commission_pct) !== String(s.commission_pct ?? '');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-12 items-center gap-1.5 rounded-lg border border-border p-1.5" data-testid={`staff-row-${s.id}`}>
      <Input className="col-span-3 h-8 text-xs" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid={`staff-name-${s.id}`} placeholder="Name" />
      <Input className="col-span-2 h-8 text-xs" value={f.phone} onChange={(e) => set('phone', e.target.value)} data-testid={`staff-phone-${s.id}`} placeholder="Phone" />
      <Input className="col-span-2 h-8 text-xs" value={f.role} onChange={(e) => set('role', e.target.value)} data-testid={`staff-role-${s.id}`} placeholder="Role" />
      <Input className="col-span-2 h-8 text-xs" type="number" value={f.monthly_target} onChange={(e) => set('monthly_target', e.target.value)} data-testid={`staff-target-${s.id}`} placeholder="Target" />
      <Input className="col-span-1 h-8 text-xs" type="number" value={f.commission_pct} onChange={(e) => set('commission_pct', e.target.value)} data-testid={`staff-pct-${s.id}`} placeholder="%" />
      <div className="col-span-2 flex items-center justify-end gap-1">
        <button disabled={!dirty || busy} onClick={() => onSave(s, { ...f, monthly_target: Number(f.monthly_target) || 0, commission_pct: Number(f.commission_pct) || 0 })}
          data-testid={`staff-save-${s.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary disabled:opacity-40"><Check className="h-4 w-4" /></button>
        <button disabled={busy} onClick={() => onDelete(s.id)} data-testid={`staff-del-${s.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function ManageStaffModal({ onClose }) {
  const qc = useQueryClient();
  const { data: staff = [], refetch } = useQuery({ queryKey: ['inc-staff'], queryFn: async () => (await api.get('/incentives/staff')).data.data });
  const [busy, setBusy] = useState(false);
  const blank = { name: '', phone: '', role: '', monthly_target: '', commission_pct: '' };
  const [nw, setNw] = useState(blank);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['inc-staff'] }); qc.invalidateQueries({ queryKey: ['inc-summary'] }); refetch(); };

  const addStaff = async () => {
    if (!nw.name.trim()) { toast.error('Enter a staff name'); return; }
    setBusy(true);
    try {
      await api.post('/incentives/staff', { ...nw, monthly_target: Number(nw.monthly_target) || 0, commission_pct: Number(nw.commission_pct) || 0 });
      setNw(blank); toast.success('Staff added'); invalidate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); } finally { setBusy(false); }
  };
  const saveRow = async (s, patch) => {
    setBusy(true);
    try { await api.put(`/incentives/staff/${s.id}`, patch); toast.success('Saved'); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const delStaff = async (id) => {
    setBusy(true);
    try { await api.delete(`/incentives/staff/${id}`); toast.success('Removed'); invalidate(); }
    catch { toast.error('Delete failed'); } finally { setBusy(false); }
  };
  const setNwF = (k, v) => setNw((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manage-staff-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>Manage Staff</DialogTitle>
          <DialogDescription>Add the people who make sales (no login needed). Name is required; phone, role, monthly target and commission % are optional. They appear as the Salesperson in billing and in incentive logs.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-12 gap-1.5 px-1.5 text-[10px] font-medium uppercase text-muted-foreground">
          <span className="col-span-3">Name</span><span className="col-span-2">Phone</span><span className="col-span-2">Role</span><span className="col-span-2">Target</span><span className="col-span-1">%</span><span className="col-span-2 text-right">Actions</span>
        </div>
        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {staff.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No staff yet. Add your first below.</p>
          ) : staff.map((s) => <StaffRow key={s.id} s={s} onSave={saveRow} onDelete={delStaff} busy={busy} />)}
        </div>
        <div className="grid grid-cols-12 items-center gap-1.5 rounded-lg border border-dashed border-border p-1.5">
          <Input className="col-span-3 h-8 text-xs" value={nw.name} onChange={(e) => setNwF('name', e.target.value)} placeholder="New staff name" data-testid="staff-new-name" onKeyDown={(e) => { if (e.key === 'Enter') addStaff(); }} />
          <Input className="col-span-2 h-8 text-xs" value={nw.phone} onChange={(e) => setNwF('phone', e.target.value)} placeholder="Phone" data-testid="staff-new-phone" />
          <Input className="col-span-2 h-8 text-xs" value={nw.role} onChange={(e) => setNwF('role', e.target.value)} placeholder="Role" data-testid="staff-new-role" />
          <Input className="col-span-2 h-8 text-xs" type="number" value={nw.monthly_target} onChange={(e) => setNwF('monthly_target', e.target.value)} placeholder="Target" data-testid="staff-new-target" />
          <Input className="col-span-1 h-8 text-xs" type="number" value={nw.commission_pct} onChange={(e) => setNwF('commission_pct', e.target.value)} placeholder="%" data-testid="staff-new-pct" />
          <div className="col-span-2 flex justify-end">
            <button onClick={addStaff} disabled={busy} data-testid="staff-add" className="flex items-center gap-1 rounded-md bg-primary px-2 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"><Plus className="h-4 w-4" />Add</button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="staff-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FixPctDialog({ current, onClose, onSaved }) {
  const [pct, setPct] = useState(String(current || 0));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post('/reports/incentive-config', { default_pct: Number(pct) }); toast.success('Incentive % saved'); onSaved && onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed (Owner only)'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fix-pct-dialog">
        <DialogHeader><DialogTitle>Fix incentive %</DialogTitle>
          <DialogDescription>Default percentage applied to new incentive entries.</DialogDescription></DialogHeader>
        <div><Label className="text-xs">Default incentive %</Label>
          <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1" data-testid="fix-pct-input" /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="fix-pct-save">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncentiveSaleModal({ mode, entry, storeId, storeName, staffOptions, defaultPct, onClose, onSaved }) {
  const [staff, setStaff] = useState(mode === 'edit' ? (entry?.staff || '') : '');
  const [date, setDate] = useState(mode === 'edit' ? (entry?.date || new Date().toISOString().slice(0, 10)) : new Date().toISOString().slice(0, 10));
  const [billNo, setBillNo] = useState(mode === 'edit' ? (entry?.bill_no || '') : '');
  const [amount, setAmount] = useState(mode === 'edit' ? String(entry?.sale_amount || '') : '');
  const [pct, setPct] = useState(mode === 'edit' ? String(entry?.incentive_pct || 0) : String(defaultPct || 0));
  const [busy, setBusy] = useState(false);
  const incentive = (Number(amount || 0) * Number(pct || 0)) / 100;
  const save = async () => {
    if (!staff) return toast.error('Select staff');
    const body = { store_id: storeId, staff, date, bill_no: billNo, sale_amount: Number(amount || 0),
      incentive_pct: Number(pct || 0), year: date.slice(0, 4), month: Number(date.slice(5, 7)) };
    setBusy(true);
    try {
      if (mode === 'edit') await api.put(`/incentives/logs/${entry.id}`, body);
      else await api.post('/incentives/logs', body);
      toast.success('Saved'); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="incentive-sale-modal">
        <DialogHeader><DialogTitle>{mode === 'edit' ? 'Edit sale' : 'Add sale'} — {storeName}</DialogTitle>
          <DialogDescription>Dashboard-only incentive entry. Does not affect billing.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Staff</Label>
            <Select value={staff} onValueChange={setStaff}>
              <SelectTrigger className="mt-1" data-testid="isale-staff"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent>{staffOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="isale-date" /></div>
          <div><Label className="text-xs">Bill no (optional)</Label><Input value={billNo} onChange={(e) => setBillNo(e.target.value)} className="mt-1" data-testid="isale-bill" /></div>
          <div><Label className="text-xs">Sale amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="isale-amount" /></div>
          <div><Label className="text-xs">Incentive %</Label><Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1" data-testid="isale-pct" /></div>
        </div>
        <p className="text-sm text-muted-foreground">Incentive: <span className="font-mono font-semibold text-primary" data-testid="isale-incentive">{money(incentive)}</span></p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="isale-save">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncentivesPage({ canManage, isOwner }) {
  const [store, setStore] = useState('all');
  const [month, setMonth] = useState('all');
  const [staffF, setStaffF] = useState('all');
  const [year, setYear] = useState('');
  const [modal, setModal] = useState(null);
  const [perfView, setPerfView] = useState('both');

  const { data, refetch } = useQuery({
    queryKey: ['inc-summary', store, month, staffF, year],
    queryFn: async () => (await api.get('/incentives/summary', { params: { store, month, staff: staffF, year: year || undefined } })).data.data,
    refetchInterval: 30000,
  });
  const { data: staffList = [] } = useQuery({ queryKey: ['inc-staff'], queryFn: async () => (await api.get('/incentives/staff')).data.data });

  const k = data?.kpi || {};
  const ps = data?.pieces_summary || {};
  const stores = data?.stores || [];
  const years = data?.years_available || [];
  const staffOptions = staffList.map((s) => s.name);
  const afterMutate = () => refetch();
  const delLog = async (id) => { try { await api.delete(`/incentives/logs/${id}`); toast.success('Deleted'); refetch(); } catch (e) { toast.error('Delete failed'); } };

  return (
    <div className="space-y-5 px-4 pt-4 pb-16" data-testid="incentives-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Incentives</p>
        <h1 className="font-display text-2xl font-semibold">Sales &amp; Incentives</h1>
      </div>

      <section className="rounded-2xl border border-border bg-card p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <FilterSel testid="inc-store" label="Stores" value={store} onChange={setStore} opts={[{ v: 'all', l: 'All' }, ...stores.map((s) => ({ v: s.id, l: s.name }))]} />
          <FilterSel testid="inc-month" label="Months" value={month} onChange={setMonth} opts={MONTHS.map((m) => ({ v: m, l: m === 'all' ? 'All' : MLABEL[m] }))} />
          <FilterSel testid="inc-staff" label="Staff" value={staffF} onChange={setStaffF} opts={[{ v: 'all', l: 'All staff' }, ...staffOptions.map((s) => ({ v: s, l: s }))]} />
          <FilterSel testid="inc-year" label="Years" value={year || (years[years.length - 1] || String(new Date().getFullYear()))} onChange={setYear} opts={(years.length ? years : [String(new Date().getFullYear())]).map((y) => ({ v: y, l: y }))} />
        </div>
        {canManage && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="inc-manage-staff" onClick={() => setModal({ type: 'staff' })} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-white/5"><Users className="h-4 w-4" /> Manage Staff</button>
            <button data-testid="inc-import" onClick={() => setModal({ type: 'import' })} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-white/5"><Upload className="h-4 w-4" /> Import Excel</button>
            <button data-testid="inc-imports-history" onClick={() => setModal({ type: 'imports' })} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-white/5"><Trash2 className="h-4 w-4" /> Undo Import</button>
            {isOwner && <button data-testid="inc-fix-pct" onClick={() => setModal({ type: 'fix' })} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-white/5"><Palette className="h-4 w-4" /> Fix Incentive %</button>}
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">Staff performance — management view</p>
        <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist" data-testid="perf-toggle">
          {[['sales', 'Sales \u20b9'], ['pieces', 'Pieces Sold'], ['both', 'Both']].map(([v, l]) => (
            <button key={v} type="button" data-testid={`perf-${v}`} role="tab" aria-selected={perfView === v} onClick={() => setPerfView(v)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${perfView === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi testid="inc-kpi-bills" label="Bills / Sales" value={(k.bills || 0).toLocaleString('en-IN')} big />
        {perfView !== 'pieces' && <Kpi testid="inc-kpi-staffsales" label="Staff Sales" value={money(k.staff_sales)} big />}
        {perfView !== 'pieces' && <Kpi testid="inc-kpi-payable" label="Incentive Payable" value={money(k.incentive_payable)} big />}
        {perfView !== 'sales' && <Kpi testid="inc-kpi-pieces" label="Total Pieces Sold" value={ps.pieces_available ? (ps.total_pieces || 0).toLocaleString('en-IN') : 'N/A'} big />}
        {perfView !== 'sales' && <Kpi testid="inc-kpi-netpieces" label="Net Pieces Sold" value={ps.pieces_available ? (ps.net_pieces || 0).toLocaleString('en-IN') : 'N/A'} sub={ps.returned_pieces ? `${ps.returned_pieces} returned` : ''} big />}
        {perfView !== 'sales' && <Kpi testid="inc-kpi-avgpieces" label="Avg Pieces / Bill" value={ps.pieces_available ? (ps.avg_pieces_per_bill ?? 0) : 'N/A'} big />}
      </div>
      {perfView !== 'sales' && ps.bills_missing_items > 0 && (
        <p className="-mt-2 text-xs text-muted-foreground" data-testid="pieces-na-note">{ps.bills_missing_items} monetary-only bill(s) in range have no item data — their pieces show as N/A (not counted as zero).</p>
      )}

      {perfView !== 'pieces' && (
        <Card chart title="Sales vs incentive by staff (\u20b9)">
          {(data?.staff_chart || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No incentive entries yet.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.staff_chart} margin={{ left: -6, right: 8, top: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="staff" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(v) => money(v)} contentStyle={tip} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sales" name="Sales \u20b9" fill="#2ecc71" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="incentive" name="Incentive \u20b9" fill="#d98032" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}

      {perfView !== 'sales' && (
        <Card chart title="Net pieces by staff (quantity)">
          {(data?.staff_pieces || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="pieces-empty">No piece data for this selection.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.staff_pieces} margin={{ left: -6, right: 8, top: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="staff" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(v, n) => [`${v} pcs`, n]} contentStyle={tip} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="net" name="Net pieces" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="returned" name="Returned" fill="#ef6b6b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}

      {perfView !== 'sales' && (data?.staff_pieces || []).length > 0 && (
        <Card title="Staff piece ranking">
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="pieces-ranking">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">#</th><th>Staff</th><th className="text-right">Gross</th><th className="text-right">Returned</th><th className="text-right">Net</th><th className="text-right">Bills</th></tr></thead>
              <tbody>
                {data.staff_pieces.map((r, i) => (
                  <tr key={r.staff} className="border-t border-border" data-testid={`pieces-rank-${r.staff}`}>
                    <td className="py-2 font-semibold">{i + 1}</td>
                    <td>{r.staff}</td>
                    <td className="text-right font-mono">{r.gross}</td>
                    <td className="text-right font-mono text-destructive">{r.returned || 0}</td>
                    <td className="text-right font-mono font-semibold">{r.net}</td>
                    <td className="text-right font-mono">{r.bills}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(data?.store_logs || []).map((sl) => (
        <Card key={sl.store_id} title={`${sl.store_name} log (${sl.count})`}
          right={canManage ? (
            <button data-testid={`inc-add-sale-${sl.store_id}`} onClick={() => setModal({ type: 'sale', mode: 'create', storeId: sl.store_id, storeName: sl.store_name })}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Add Sale</button>
          ) : null}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid={`inc-log-${sl.store_id}`}>
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Date</th><th>Staff</th><th>Bill No</th><th>Sale Amount</th><th>Incentive %</th><th>Incentive</th><th></th></tr></thead>
              <tbody>
                {sl.rows.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No sales logged. Add a sale to start tracking incentives.</td></tr>
                ) : sl.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`inc-row-${r.id}`}>
                    <td className="py-2 text-xs">{r.date}</td>
                    <td>{r.staff}</td>
                    <td className="text-xs">{r.bill_no || '\u2014'}</td>
                    <td className="font-mono">{money(r.sale_amount)}</td>
                    <td className="font-mono">{r.incentive_pct}%</td>
                    <td className="font-mono text-primary">{money(r.incentive)}</td>
                    <td>
                      {canManage && (
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`inc-edit-${r.id}`} onClick={() => setModal({ type: 'sale', mode: 'edit', entry: r, storeId: sl.store_id, storeName: sl.store_name })} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`inc-del-${r.id}`} onClick={() => delLog(r.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {(data?.billing_staff || []).length > 0 && (
        <Card title="From billing (auto \u00b7 read-only)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="inc-billing-ref">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Staff</th><th>Billing Sales</th></tr></thead>
              <tbody>
                {data.billing_staff.map((b, i) => (<tr key={i} className="border-t border-border"><td className="py-2">{b.staff}</td><td className="font-mono">{money(b.sales)}</td></tr>))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {modal?.type === 'staff' && <ManageStaffModal onClose={() => setModal(null)} />}
      {modal?.type === 'import' && (
        <DashBulkImportDialog stores={stores} onClose={() => setModal(null)} onSaved={afterMutate} cfg={{
          title: 'Bulk Import Incentives', noun: 'incentive',
          validateUrl: '/incentives/logs/import/validate',
          commitUrl: '/incentives/logs/import/commit',
          templateUrl: '/incentives/logs/template', templateFile: 'Staff Incentives Import Template.xlsx',
          totalsFields: [{ key: 'sale_amount', label: 'Total sales' }, { key: 'incentive', label: 'Total incentive' }],
          desc: 'Upload XLSX/CSV of staff incentives (columns: date, staff, store, bill_no, sale_amount, incentive_pct). Invalid rows are skipped. Nothing is saved until you confirm, and you can roll back a batch later.',
        }} />
      )}
      {modal?.type === 'fix' && <FixPctDialog current={data?.default_pct || 0} onClose={() => setModal(null)} onSaved={afterMutate} />}
      {modal?.type === 'imports' && <ImportHistoryModal onClose={() => setModal(null)} onSaved={afterMutate}
        historyUrl="/incentives/logs/import/history" rollbackUrl="/incentives/logs/import/rollback" remapUrl="/incentives/logs/import/remap" unit="incentive row"
        desc="Remove every incentive row a past Excel import added. Sales logged automatically from billing are never affected." />}
      {modal?.type === 'sale' && <IncentiveSaleModal mode={modal.mode} entry={modal.entry} storeId={modal.storeId} storeName={modal.storeName} staffOptions={staffOptions} defaultPct={data?.default_pct || 0} onClose={() => setModal(null)} onSaved={afterMutate} />}
    </div>
  );
}


function ImportHistoryModal({ onClose, onSaved, historyUrl, rollbackUrl, remapUrl, unit = 'row', title = 'Undo an import', desc }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const nowY = new Date().getFullYear();
  const [mYear, setMYear] = useState(String(nowY));
  const [mMonth, setMMonth] = useState(String(new Date().getMonth() + 1));
  const load = async () => {
    try { setRows((await api.get(historyUrl)).data.data || []); }
    catch (e) { toast.error('Could not load import history'); setRows([]); }
  };
  React.useEffect(() => { load(); }, []);
  const doRollback = async (b) => {
    setBusy(b.batch_id || b.id);
    try {
      const r = await api.post(rollbackUrl, { batch_id: b.batch_id || b.id });
      toast.success(`Import undone — removed ${r.data.data.removed} ${unit}${r.data.data.removed === 1 ? '' : 's'}`);
      setConfirm(null);
      await load();
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Undo failed'); }
    finally { setBusy(''); }
  };
  const openMove = (b) => {
    const parts = (b.moved_to || (b.created_at || '')).slice(0, 7).split('-');
    setMYear(parts[0] || String(nowY));
    setMMonth(String(parseInt(parts[1], 10) || (new Date().getMonth() + 1)));
    setMoveFor(b);
  };
  const doMove = async () => {
    setBusy(moveFor.batch_id || moveFor.id);
    try {
      const r = await api.post(remapUrl, { batch_id: moveFor.batch_id || moveFor.id, year: Number(mYear), month: Number(mMonth) });
      toast.success(`Moved ${r.data.data.updated} ${unit}${r.data.data.updated === 1 ? '' : 's'} to ${MONTHS[Number(mMonth) - 1]} ${mYear}`);
      setMoveFor(null);
      await load();
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Move failed'); }
    finally { setBusy(''); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="import-history-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{desc || `Remove every ${unit} a past Excel import added. Entries created manually or from billing are never affected.`}{remapUrl ? ' Wrong month? Use Move to shift a whole batch without re-uploading.' : ''}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {rows === null ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No imports yet.</p>
          ) : rows.map((b) => {
            const rolled = b.status === 'ROLLED_BACK';
            const count = b.added_count ?? b.created ?? b?.result?.created ?? b?.result?.added ?? 0;
            return (
              <div key={b.id} data-testid={`import-row-${b.batch_id || b.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-border p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{b.filename || 'import.xlsx'}</p>
                  <p className="text-xs text-muted-foreground">{(b.created_at || '').slice(0, 16).replace('T', ' ')} · {count} {unit}{count === 1 ? '' : 's'}{b.created_by ? ` · ${b.created_by}` : ''}{b.moved_to ? ` · moved to ${b.moved_to}` : ''}</p>
                </div>
                {rolled ? (
                  <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">Undone</span>
                ) : (
                  <div className="flex shrink-0 items-center gap-1.5">
                    {remapUrl && (
                      <Button size="sm" variant="secondary" disabled={!!busy} data-testid={`import-move-${b.batch_id || b.id}`} onClick={() => openMove(b)}>
                        <CalendarDays className="mr-1 h-4 w-4" />Move
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" disabled={!!busy} data-testid={`import-undo-${b.batch_id || b.id}`} onClick={() => setConfirm(b)}>
                      <Trash2 className="mr-1 h-4 w-4" />Undo
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="import-history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="import-undo-confirm">
          <DialogHeader>
            <DialogTitle>Undo this import?</DialogTitle>
            <DialogDescription>This removes the {confirm?.added_count ?? confirm?.created ?? confirm?.result?.created ?? confirm?.result?.added ?? 0} {unit}(s) added by “{confirm?.filename}”. This can’t be reversed.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!!busy} onClick={() => doRollback(confirm)} data-testid="import-undo-confirm-btn">Undo import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!moveFor} onOpenChange={(o) => !o && setMoveFor(null)}>
        <DialogContent data-testid="import-move-dialog">
          <DialogHeader>
            <DialogTitle>Move import to a month</DialogTitle>
            <DialogDescription>Set the correct month for every {unit} from “{moveFor?.filename}”. The day of each entry is kept; only the month and year change.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Month</p>
              <Select value={mMonth} onValueChange={setMMonth}>
                <SelectTrigger data-testid="import-move-month" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((mn, i) => <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Year</p>
              <Select value={mYear} onValueChange={setMYear}>
                <SelectTrigger data-testid="import-move-year" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{[nowY + 1, nowY, nowY - 1, nowY - 2, nowY - 3].map((yy) => <SelectItem key={yy} value={String(yy)}>{yy}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMoveFor(null)}>Cancel</Button>
            <Button disabled={!!busy} onClick={doMove} data-testid="import-move-confirm-btn"><CalendarDays className="mr-1 h-4 w-4" />Move here</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


export default function ManagementDashboard() {
  const { user, logout, can } = useApp();
  const [view, setView] = useState('dashboard');
  const navigate = useNavigate();
  const isPartner = user?.role === 'PARTNER';
  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.owner_access;
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const allowed = isPartner || isOwner || (can && can('dashboard')) || (can && can('reports_view'));
  React.useEffect(() => {
    if (user && !allowed) { toast.error('You don’t have access to the Management Dashboard'); navigate('/'); }
  }, [user, allowed, navigate]);
  const roleLabel = { OWNER: 'Owner', MANAGER: 'Manager', PARTNER: 'Partner', STAFF: 'Staff' }[user?.role] || 'User';

  if (user && !allowed) return null;

  return (
    <div className="app-bg min-h-screen" data-testid="management-shell">
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-card/95 px-3 py-2.5 backdrop-blur sm:px-4 sm:py-3 no-print"
        style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-primary-foreground">S</span>
          <span className="hidden font-display text-sm font-semibold sm:inline">SHABZ CLOSET</span>
        </div>
        <span className="ml-1 hidden text-xs text-muted-foreground md:inline" data-testid="shell-user">{user?.username} · {roleLabel}</span>
        <div className="ml-auto flex items-center gap-2">
          {view === 'incentives' ? (
            <button data-testid="inc-back" onClick={() => setView('dashboard')}
              className="flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-white/5">
              <ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Dashboard</span>
            </button>
          ) : canManage && <DashboardActions isOwner={isOwner} onIncentives={() => setView('incentives')} />}
          {!isPartner && (
            <button data-testid="back-to-app" onClick={() => navigate('/')}
              className="flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-white/5"
              title="Back to billing app">
              <ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">App</span>
            </button>
          )}
          <button data-testid="shell-logout" onClick={logout}
            className="flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-white/5"
            title="Sign out">
            <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl">
        {view === 'incentives'
          ? <IncentivesPage canManage={canManage} isOwner={isOwner} />
          : <DashboardBody onIncentives={() => setView('incentives')} />}
      </main>
    </div>
  );
}
